// TODO: Add publications
// TODO: Add sorting by year (with "in press" at the start, and "in preparation" at the end)
/**
 * @typedef {Object} BookData
 * @param {string} [publisher]
 * @param {string} [city]
 * @param {string} [link]
 * @param {any} [description]
 */
/**
 * @typedef {BookData} ChapterData
 * @param {string} [book]
 * @param {string} [pages]
 * @param {string[]} [editors]
 * @param {any} [abstract]
 */
/**
 * @typedef {Object} ArticleData
 * @param {string} [journal]
 * @param {string} [volume]
 * @param {string} [issue]
 * @param {string} [pages]
 * @param {string} [link]
 * @param {string[]} [keywords]
 * @param {any} [abstract]
 */
/**
 * @typedef {Object} Title
 * @property {string} primary
 * @property {string} secondary
 */
/**
 * @typedef {Object} Publication
 * @param {string} type
 * @param {number|string} year
 * @param {Title} title
 * @param {string[]} authors
 * @param {ChapterData|ArticleData|BookData} [data]
 */
/**
 * @type {Publication[]}
 */
export const publications = [
  {
    title: {
      primary: "From corpus to classroom",
      secondary: "Evaluating Web-based tools to teach collocations",
    },
    authors: ["Goulart, L.", "Klein, J.", "Kostromitina, M."],
    year: "in press",
    type: "chapter",
    /**
     * @type {ChapterData}
     */
    data: {
      book: "English for academic purposes: Reflections, description, and pedagogy",
      publisher: "Universidade Federal do Rio Grande do Sul",
    },
  },
  {
    title: {
      primary:
        "An exploratory study of intonation variation in L1 and L2 English speakers' pragmatic production of high imposition requests and refusals",
      secondary: "",
    },
    authors: ["Kostromitina, M."],
    year: "in press",
    type: "article",
    /**
     * @type {ArticleData}
     */
    data: {
      journal: "Applied Pragmatics",
      volume: "",
      number: "",
      page: "",
      link: "",
      keywords: [],
      abstract: (
        <>
          This exploratory study examined the intonational patterns and
          corresponding speech act strategies in L1 and L2 English speakers'
          production of high imposition requests and refusals that were elicited
          using video-based multiple turn discourse completion tasks (DCTs). The
          participants, 34 L1 American English speakers and 14 high-proficiency
          L2 English speakers, recorded their oral responses to 10 multiple-turn
          video-based DCTs. Using Brazil's (1997) framework, the collected
          speech samples were analyzed for tone choices in each tone unit.
          Pragmatic strategies in the produced speech acts were analyzed by
          adapting the existing coding frameworks (Beebe et al., 1990;
          Blum-Kulka et al., 1989). The extracted tone choices and
          lexico-grammatical strategies were compared between the two language
          groups. The results preliminarily revealed differences in the use of
          tones by L1 and L2 English speakers in performing requests and
          refusals. Moreover, while the employed speech act strategies were
          largely similar between the two speaker groups, the associated tone
          choices showed contrasting usage patterns.
        </>
      ),
    },
  },
  {
    title: {
      primary: "Cross-linguistic transfer in academic journal writing",
      secondary: "Evidence from lexical bundle analysis in Russian and English",
    },
    authors: ["Kostromitina, M."],
    year: "in press",
    type: "article",
    /**
     * @type {ArticleData}
     */
    data: {
      journal: "Research in Corpus Linguistics",
      abstract: (
        <>
          Lexical bundles are considered a fundamental feature of the register
          of academic writing and have been extensively studied by corpus
          linguists. However, while learner corpus-based studies have noted the
          differences between first (L1) and second languages (L2) in the
          production of lexical bundles, few of them have assessed the
          underlying causes of such differences, particularly regarding
          cross-linguistic transfer. The present study investigates the use of
          lexical bundles in professional writing in the field of Educational
          Psychology produced by L1 English and L1 Russian authors in order to
          evaluate the evidence of cross-linguistic transfer in the writing of
          L2 English learners with L1 Russian background and examine the
          patterns of L2 English lexical bundle use that mirror L1 English
          production. This exploratory study compares the frequency and
          discourse functions of lexical bundles produced by native speakers of
          English to those used by Russian speakers in their L2 English
          professional writing, as well as professional writing in their L1. The
          results of the study indicate that L2 English writing produced by
          Russian speakers displays overlap in the composition and use of
          lexical bundles in L1 Russian writing pointing at possible L1
          transfer.
        </>
      ),
    },
  },
  {
    title: {
      primary:
        "The effects of situational contexts and occupational roles on listeners' judgements on accented speech",
      secondary: "",
    },
    authors: ["Kang, O.", "Yaw, K.", "Kostromitina, M."],
    year: "in press",
    type: "article",
    /**
     * @type {ArticleData}
     */
    data: {
      journal: "Psychology of Language and Communication",
      volume: "",
      number: "",
      page: "",
      link: "",
      keywords: [],
      abstract: (
        <>
          Much language attitude research has demonstrated that people make
          biased judgements based on speakers' language choice and accent.
          However, the influence of occupational context on listeners'
          perceptions of accented English is largely unknown. This verbal guise
          study examined to what extent academic contexts and workforce-related
          professional contexts affected listeners' judgements of accented
          speech. Results revealed that simulated contexts made a significant
          difference in listeners' perceptual judgements, with speakers
          perceived as significantly more comprehensible and acceptable in
          service-occupational roles than in academic contexts. Findings suggest
          that listeners' speech judgements can be heavily influenced by
          speakers' situational contexts. The study also provides evidence in
          support of the fluency principle showing that listeners may evaluate
          accented speech more negatively if it requires more processing effort.
          The findings inform the domains of linguistic stereotyping and
          listeners' attitudes towards accented speech.
        </>
      ),
    },
  },
  {
    title: {
      primary: "Prosody and pragmatics in applied linguistics",
      secondary: "",
    },
    authors: ["Kostromitina, M.", "Kermad, A."],
    year: "in press",
    type: "chapter",
    /**
     * @type {ChapterData}
     */
    data: {
      book: "The Encyclopedia of Applied Linguistics",
      editors: ["C. Chapelle"],
      publisher: "Wiley",
      city: "",
      description: (
        <>
          Prosodic properties of a language bear multiple functions. One of such
          functions is to signal pragmatic meaning in spoken discourse. Speakers
          produce various prosodic patterns to achieve communicative goals by
          placing prominence onto selected syllables, changing the speed of
          their speech, or using rising, falling, or level tones. This
          relationship between prosodic patterns and discourse is fundamental to
          interpreting pragmatic meaning and, as such, has been observed in
          several pragmatic discourse elements including speech acts,
          (dis)agreements, and feedback markers by first (L1) and second (L2)
          language speakers across languages. Although pragmatic functions of
          prosody are intuitive for L1 speakers, L2 learners are oftentimes
          unaware of the underlying conventions of prosodic use in a given
          language. Due to this unintuitive nature of prosody for L2 learners,
          deviations in learners' prosodic patterns may be misinterpreted by
          interlocutors and may have negative pragmatic consequences in a given
          communicative situation. This chapter defines the relationship between
          prosody and pragmatics in discourse and provides a brief overview of
          the current research at the pragma-prosodic interface. It focuses on
          the prosody of speech acts highlighting differences in L1 and L2
          perception and production of prosody. Important constructs related to
          pragma-prosodic success are highlighted. The chapter concludes with an
          appraisal of the current literature and an outline of directions for
          future research and pedagogical applications for L2 instruction.
        </>
      ),
    },
  },
  {
    title: {
      primary: "Individual learner characteristics and pragmatic competence",
      secondary: "",
    },
    authors: ["Taguchi, N.", "Kostromitina, M.", "Wheeler, H."],
    year: 2022,
    type: "chapter",
    /**
     * @type {ChapterData}
     */
    data: {
      book: "The Routledge Handbook of Second Language Acquisition and Individual Differences",
      editors: ["S. Li", "P. Hiver", "M. Papi"],
      publisher: "Routledge",
      city: "",
      description: (
        <>
          This chapter presents a systematic review of empirical studies that
          examined the impact of individual learner characteristics on pragmatic
          competence and development. The goal of the chapter is two-fold: 1) to
          describe and critically analyze empirical practices in the domain of
          individual learner characteristics in L2 pragmatics, and 2) to examine
          the overall effects of a select learner characteristic (i.e.,
          proficiency) on pragmatic competence. The first goal is pursued using
          a scoping review that surveys existing studies in terms of their
          quantity, range, and characteristics (e.g., research designs and
          reporting practices). The second goal is addressed by adopting a
          small-scale meta-analysis focusing on proficiency as a learner
          characteristic.
        </>
      ),
    },
  },
  {
    title: {
      primary:
        "The effects of ESL immersion and proficiency on learners' pronunciation development",
      secondary: "",
    },
    authors: ["Kostromitina, M.", "Kang, O."],
    year: 2021,
    type: "article",
    /**
     * @type {ArticleData}
     */
    data: {
      journal: "Frontiers in Communication",
      volume: "6",
      number: "103048",
      page: "1-14",
      link: "https://doi.org/10.3389/fcomm.2021.636122",
      keywords: [
        "ESL immersion",
        "fluency",
        "functional load",
        "L2 proficiency",
        "L2 pronunciation",
      ],
      abstract: (
        <>
          Despite the efforts of existing studies in the domain of L2 phonology
          to examine ESL learners' pronunciation development, little research
          has comprehensively demonstrated ESL learners' pronunciation
          improvement in academic immersion contexts. Similarly, few studies
          have focused on learners' proficiency levels linked to their
          developmental success. The current exploratory study investigated the
          changes of learners' pronunciation constructs as a result of their ESL
          program. Seventy-five newly arrived ESL students (25 in each
          proficiency; beginner, intermediate, and advanced) enrolled in an
          Intensive English Program in the United States provided their speech
          responses (to the placement and exit tests from the program). One
          hundred fifty speaking samples were linguistically analyzed for the
          following suprasegmental features: fluency (speech rates and pauses)
          and prosody (prominence and pitch range). Segmental features were
          analyzed by employing a functional load approach with randomly
          selected 90 speech files. Findings revealed different developmental
          patterns among phonological features and proficiency levels; that is,
          the upper-level learners improved more in fluency and prominence than
          the lower-level learners. Segmental changes were minimal, suggesting
          that both high functional and low functional load sounds involve a
          complex process in learning. Overall findings provide important
          implications for ESL curriculum planning and development: 1)
          intonation acquisition can be difficult; 2) skill improvement differs
          by proficiency level; and 3) level-specific curriculum may be needed.
        </>
      ),
    },
  },
  {
    title: {
      primary: "Elicited imitation tasks as a measure of L2 proficiency",
      secondary: "A meta-analysis",
    },
    authors: ["Kostromitina, M.", "Plonsky, L."],
    year: 2021,
    type: "article",
    /**
     * @type {ArticleData}
     */
    data: {
      journal: "Studies in Second Language Acquisition",
      volume: "3",
      number: "44",
      page: "886-911",
      link: "https://doi.org/10.1017/S0272263121000395",
      keywords: [],
      abstract: (
        <>
          Elicited imitation tasks (EITs) have been proposed and examined as a
          practical measure of second language (L2) proficiency. This study
          aimed to provide an updated and comprehensive view of the relationship
          between EITs and other proficiency measures. Toward that end, 46
          reports were retrieved contributing 60 independent effect sizes
          (Pearson's r) that were weighted and averaged. Several EIT features
          were also examined as potential moderators. The results portray EIT as
          a generally consistent measure of L2 proficiency (r = .66). Among
          other moderators, EIT stimuli length was positively associated with
          stronger correlations. Overall, the findings provide support for the
          use of EITs as a means to greater consistency and practicality in
          measuring L2 proficiency. In our Discussion section, we highlight the
          need for more transparent reporting and provide empirically grounded
          recommendations for EIT design and for further research into EIT
          development.
        </>
      ),
    },
  },
  {
    title: {
      primary:
        '"His lack of a mask ruined everything." Restaurant customer satisfaction during the COVID-19 outbreak',
      secondary: "An analysis of Yelp review texts and star-ratings",
    },
    authors: ["Kostromitina, M.", "Keller, D.", "Cavusoglu, M.", "Beloin, K"],
    year: 2021,
    type: "article",
    /**
     * @type {ArticleData}
     */
    data: {
      journal: "International Journal of Hospitality Management",
      volume: "98",
      number: "103048",
      page: "",
      link: "https://doi.org/10.1016/j.ijhm.2021.103048",
      keywords: [
        "Corpus linguistics",
        "Independent restaurants",
        "Keyword analysis",
        "Multiple correspondence analysis",
        "Online reviews",
        "Pandemic",
      ],
      abstract: (
        <>
          The aim of the study was to provide practical advice to restaurant
          managers for improving star ratings as well as information for
          researchers on how the pandemic has impacted established determinants
          of satisfaction. The study examined criteria used by restaurant
          customers in assigning star-ratings on Yelp during the COVID-19
          pandemic using keyword analysis and Multiple Correspondence Analysis.
          In evaluating restaurants, the reviewers focused on service, overall
          experience, and food quality. Service was discussed in relation to the
          pandemic and included safety of the dine-in experience, contrasted
          with take-out options and compliance with COVID-19 guidelines. These
          criteria applied differently with lower-star reviews focusing on
          safety, social distancing, and mask policies. Higher-star reviews
          focused on take-out/delivery services, high-quality food, and an
          overall positive experience. The study provides valuable contributions
          to our understanding of how the COVID-19 pandemic will impact the
          restaurant sector in a post-pandemic world.
        </>
      ),
    },
  },
  {
    title: {
      primary: "Characterizing non-chain restaurants' Yelp star-ratings",
      secondary:
        "Generalizable findings from a representative sample of Yelp reviews",
    },
    authors: ["Keller, D.", "Kostromitina, M."],
    year: 2020,
    type: "article",
    /**
     * @type {ArticleData}
     */
    data: {
      journal: "International Journal of Hospitality Management",
      volume: "86",
      number: "102440",
      page: "",
      link: "https://doi.org/10.1016/j.ijhm.2019.102440",
      keywords: [
        "Restaurant success",
        "Review criteria",
        "Yelp star rating",
        "Determinants of satisfaction",
        "Multiple correspondence analysis",
        "Wait time",
        "Food quality",
      ],
      abstract: (
        <>
          Previous research has established the importance of positive Yelp
          reviews for the success of independent, non-chain restaurant. While
          managers may assume that increasing customer satisfaction will
          increase star rating, previous research on determinants of
          satisfaction does not necessarily generalize to Yelp reviews or across
          Yelp star ratings. Accordingly, we used Multiple Correspondence
          Analysis with a representative sample of 54,000 reviews to investigate
          the criteria used by reviewers in their assignment of star-ratings to
          determine if different star-ratings are characterized by different
          criteria. Our findings confirm previous research showing the
          importance of service, food, and environment, but suggest these
          criteria matter differently at different star ratings. Based on these
          results, we offer suggestions for improving restaurants' star-ratings:
          for 1 and 2-star restaurants, avoid long waits, for 3-star
          restaurants, focus on improving food quality, for 4 and 5-star
          restaurants, focus on creating a positive overall experience through
          service, food, and environmental factors.
        </>
      ),
    },
  },
  {
    title: {
      primary:
        "The use of scales in measuring L2 accentedness, comprehensibility, and intelligibility",
      secondary: "A systematic review and meta-analysis",
    },
    authors: ["Kostromitina, M.", "Sudina, E."],
    year: "in preparation",
    type: "article",
    /**
     * @type {ArticleData}
     */
    data: {
      journal: "",
      editors: "",
      publisher: "",
      city: "",
      abstract: "",
    },
  },
  {
    title: {
      primary: "Linguistic features of academic spoken English",
      secondary: "An SEM approach",
    },
    authors: ["Gourlart, L.", "Kostromitina, M."],
    year: "in preparation",
    type: "article",
    /**
     * @type {ArticleData}
     */
    data: {
      journal: "",
      editors: "",
      publisher: "",
      city: "",
      abstract: "",
    },
  },
  {
    title: {
      primary: "Transitional language in spoken discourse",
      secondary: "Evidence from keyword and lexical bundle analyses",
    },
    authors: ["Kostromitina, M.", "Keller, D."],
    year: "in preparation",
    type: "article",
    /**
     * @type {ArticleData}
     */
    data: {
      journal: "",
      editors: "",
      publisher: "",
      city: "",
      abstract: "",
    },
  },
  {
    title: {
      primary: "Listeners' perceptions of appropriateness of English refusals",
      secondary: "",
    },
    authors: ["Kostromitina, M.", "Miao, V."],
    year: "in preparation",
    type: "article",
    /**
     * @type {ArticleData}
     */
    data: {
      journal: "",
      editors: "",
      publisher: "",
      city: "",
      abstract: "",
    },
  },
  {
    title: {
      primary:
        "Test-takers attitudes towards varieties of accents in listerning tasks of the Duolingo English Test",
      secondary: "",
    },
    authors: [
      "Kang, O.",
      "Kostromitina, M.",
      "Yan, X.",
      "Thomson, R.",
      "Isaacs, T.",
    ],
    year: "under review",
    type: "article",
    /**
     * @type {ArticleData}
     */
    data: {
      journal: "TESOL Quarterly",
      volume: "",
      number: "",
      page: "",
      link: "",
      keywords: [],
      abstract: (
        <>
          The present study explored test-takers' overall attitudes towards the
          inclusion of different English accents in the Duolingo English Test
          (DET) and their associations with listening test scores. One
          hundred-sixty English learners from four language backgrounds
          (Chinese, Korean, Indian English (Hindi), and Spanish) completed
          yes/no vocabulary and dictation tasks that simulated the DET listening
          sections. The tasks included speech produced by English speakers of
          the same language background as the listeners, as well as American and
          British English. Learners also completed an extensive survey that
          elicited their attitudes towards non-standard English accents in
          proficiency tests. Exploratory factor analysis of survey responses
          revealed two contrasting trends in learners' attitudes. Constructed
          responses further suggested that while listeners generally preferred
          prestigious English models (e.g., American English) (over 80%), they
          also expressed a need for incorporating other accent varieties. The
          relationships between listeners' attitudes and their performance on
          the test were minimal (r &lt; .26). The findings of the study shed
          light on our understanding of test takers' needs regarding accent
          varieties in listening tests.
        </>
      ),
    },
  },

  {
    title: {
      primary: "Fairness of using different English accents",
      secondary:
        "The effect of shared L1s in listening tasks of the Duolingo English Test",
    },
    authors: [
      "Kang, O.",
      "Kostromitina, M.",
      "Yan, X.",
      "Thomson, R.",
      "Isaacs, T.",
    ],
    year: "under review",
    type: "article",
    /**
     * @type {ArticleData}
     */
    data: {
      journal: "Language Testing",
      volume: "",
      number: "",
      page: "",
      link: "",
      keywords: [],
      abstract: (
        <>
          The study aimed to answer an ongoing validity question related to the
          use of non-standard English accents in international tests of English
          proficiency and associated issues of test fairness (Hamp-Lyons &
          Davies, 2008). More specifically, the study examined (1) the extent to
          which different or shared English accents had an impact on listeners'
          performance on the Duolingo listening tests and (2) the extent to
          which different English accents affected listeners' performances on
          two different task types). Speakers from four English accent varieties
          (Chinese, Spanish, Indian English [Hindi] and Korean) produced speech
          samples for 'yes/no' vocabulary and dictation Duolingo listening
          tasks. Listeners who spoke with these same four English accents were
          then recruited to take the Duolingo listening test items. Results
          indicated that there is a shared-L1 benefit effect overall, with
          comparable test scores between shared-L1 and inner-circle L1 accents,
          and no significant differences in listeners' listening performance
          scores across highly intelligible accent varieties. No task type
          effect was found. The findings provide guidance to better understand
          fairness, equality, and practicality of designing and administering
          high-stakes English tests targeting a diversity of accents.
        </>
      ),
    },
  },
  {
    title: {
      primary: "Understanding spoken discourse",
      secondary:
        "Linguistic analysis for second language research and teaching",
    },
    authors: ["Kang, O.", "Ghanem, R.", "Biber, D.", "Kostromitina, M."],
    year: "under contract",
    type: "book",
    /**
     * @type {BookData}
     */
    data: {
      editors: "",
      publisher: "Routledge",
      city: "",
      description: "",
    },
  },
];

export default publications;

const sortPublicationsByType = (type) => {
  switch (type) {
    case "book":
      return 9999;
    case "journal":
      return 9998;
    case "chapter":
      return 9997;
    default:
      return 9997;
  }
};

const sortPublicationsByYears = (year) => {
  switch (year) {
    case "under contract":
      return 9999;
    case "under review":
      return 9999;
    case "in press":
      return 9998;
    case "in preparation":
      return -1;
    default:
      return year instanceof String ? parseInt(year) ?? year : year;
  }
};

const sortPublicationsByAuthors = (authors) => {
  let first = authors[0];
  // sort by Kostrimitina as first author, then by last name
  if (first === "Kostrimitina, M.") {
    return -1;
  } else {
    return first.charCodeAt(0);
  }
};
/**
 *
 * @param {string|number} a
 * @param {string|number} b
 * @returns {number}
 *
 */
export function sortPublications(a, b) {
  let sortedByType =
    sortPublicationsByType(b.type) - sortPublicationsByType(a.type);
  if (sortedByType !== 0) {
    return sortedByType;
  }
  let sortedByYear =
    sortPublicationsByYears(b.year) - sortPublicationsByYears(a.year);
  return sortedByYear === 0
    ? sortPublicationsByAuthors(b.authors) -
        sortPublicationsByAuthors(a.authors)
    : sortedByYear;
}
